import React, { useEffect, useState } from "react";
import autoTable from "jspdf-autotable";
import "../css/InvoiceGenerator.css";
import {
  GENERATOR_PACKAGES,
  INVOICE_ADD_ONS,
} from "../utils/eventCalendarDocumentPrefill";
import {
  applyDocumentHeader,
  createDocumentPdf,
  DOCUMENT_PDF_TABLE_OPTIONS,
} from "../utils/documentPdf";
import {
  CARD_FEE_RATE,
  formatCurrency,
  TAX_RATE,
} from "../utils/bookingCalculations";

import { ADDITIONAL_CHILD_PRICE, ADDITIONAL_ADULT_PRICE, describePackage } from "../constants/packageDefaults";
import { calculateInvoiceCharges, hasAgreedEventTotal, itemQuantity, itemTotal } from "../utils/invoiceCalculations";
import { guestCount } from "../utils/bookingPricing";

const InvoiceGenerator = ({ prefillRequest = null }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [pricingNeedsReview, setPricingNeedsReview] = useState(false);
  const [agreedEventTotal, setAgreedEventTotal] = useState("");
  const [historicalPackageName, setHistoricalPackageName] = useState("Booking");
  const [additionalKids, setAdditionalKids] = useState(0);
  const [additionalAdults, setAdditionalAdults] = useState(0);
  const [addOnSelections, setAddOnSelections] = useState([]);

  // Custom items
  const [customItems, setCustomItems] = useState([]);
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [customQty, setCustomQty] = useState(1);

  const [customerName, setCustomerName] = useState("");
  const [childName, setChildName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventStartTime, setEventStartTime] = useState("");
  const [eventEndTime, setEventEndTime] = useState("");
  const [discount, setDiscount] = useState(0);
  const [depositPaid, setDepositPaid] = useState(0);
  const [notes, setNotes] = useState("");
  const [appliedPrefillKey, setAppliedPrefillKey] = useState("");

  // Toggles
  const [removeTax, setRemoveTax] = useState(false);
  const [cardFeeEnabled, setCardFeeEnabled] = useState(false);
  const [smallFileMode, setSmallFileMode] = useState(true);

  const resetForm = () => {
    setSelectedPackage(null);
    setPricingNeedsReview(false);
    setAgreedEventTotal("");
    setAdditionalKids(0);
    setAdditionalAdults(0);
    setAddOnSelections([]);
    setCustomItems([]);
    setCustomName("");
    setCustomPrice("");
    setCustomQty(1);
    setCustomerName("");
    setChildName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setEventDate("");
    setEventStartTime("");
    setEventEndTime("");
    setDiscount(0);
    setDepositPaid(0);
    setNotes("");
    setRemoveTax(false);
    setCardFeeEnabled(false);
    setSmallFileMode(true);
  };

  useEffect(() => {
    if (!prefillRequest?.requestKey) return;
    if (prefillRequest.requestKey === appliedPrefillKey) return;

    if (!prefillRequest.data) {
      resetForm();
      setAppliedPrefillKey(prefillRequest.requestKey);
      return;
    }

    const next = prefillRequest.data;
    setSelectedPackage(next.selectedPackage || null);
    setPricingNeedsReview(Boolean(next.pricingNeedsReview));
    setHistoricalPackageName(next.historicalPackageName || "Booking");
    setAgreedEventTotal("");
    setAdditionalKids(Number(next.additionalKids || 0));
    setAdditionalAdults(Number(next.additionalAdults || 0));
    setAddOnSelections(next.addOnSelections || []);
    setCustomItems(next.customItems || []);
    setCustomName("");
    setCustomPrice("");
    setCustomQty(1);
    setCustomerName(next.customerName || "");
    setChildName(next.childName || "");
    setCustomerPhone(next.customerPhone || "");
    setCustomerEmail(next.customerEmail || "");
    setEventDate(next.eventDate || "");
    setEventStartTime(next.eventStartTime || "");
    setEventEndTime(next.eventEndTime || "");
    setDiscount(Number(next.discount || 0));
    setDepositPaid(Number(next.depositPaid || 0));
    setNotes(next.notes || "");
    setRemoveTax(Boolean(next.removeTax));
    setCardFeeEnabled(Boolean(next.cardFeeEnabled));
    setSmallFileMode(true);
    setAppliedPrefillKey(prefillRequest.requestKey);
  }, [appliedPrefillKey, prefillRequest]);

  const handleAddOnToggle = (addOnName) => {
    if (addOnSelections.find((item) => item.name === addOnName)) {
      setAddOnSelections(addOnSelections.filter((i) => i.name !== addOnName));
    } else {
      const addOn = INVOICE_ADD_ONS.find((a) => a.name === addOnName);
      setAddOnSelections([
        ...addOnSelections,
        { name: addOnName, price: addOn.price, quantity: 1 },
      ]);
    }
  };

  const handleAddOnQuantityChange = (addOnName, quantity) => {
    setAddOnSelections(
      addOnSelections.map((item) =>
        item.name === addOnName
          ? { ...item, quantity: parseInt(quantity, 10) || 1 }
          : item
      )
    );
  };

  const handleAddOnPriceChange = (addOnName, price) => {
    setAddOnSelections(
      addOnSelections.map((item) =>
        item.name === addOnName
          ? { ...item, price: parseFloat(price) || 0 }
          : item
      )
    );
  };

  const addCustomItem = () => {
    const priceNum = parseFloat(customPrice);
    const qtyNum = parseInt(customQty, 10) || 1;
    if (!customName.trim() || !Number.isFinite(priceNum) || priceNum < 0)
      return;
    setCustomItems((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: customName.trim(),
        price: priceNum,
        quantity: qtyNum,
      },
    ]);
    setCustomName("");
    setCustomPrice("");
    setCustomQty(1);
  };

  const removeCustomItem = (id) => {
    setCustomItems((prev) => prev.filter((i) => i.id !== id));
  };

  const formatMoney = formatCurrency;

  const calculateTotals = () => calculateInvoiceCharges({
    selectedPackage, additionalKids, additionalAdults, addOnSelections,
    customItems, discount, depositPaid, removeTax, cardFeeEnabled,
    pricingNeedsReview, agreedEventTotal,
  });

  const calculateTotal = () => {
    const { total } = calculateTotals();
    return (total || 0).toFixed(2);
  };

  const generatePDF = () => {
    if (pricingNeedsReview && !hasAgreedEventTotal(agreedEventTotal)) {
      alert("Enter the agreed event total from the original booking before generating this invoice.");
      return;
    }
    if (!selectedPackage && !pricingNeedsReview) {
      alert("Please select a package before generating the invoice.");
      return;
    }

    const { subtotal, tax, balanceBeforeCard, cardFee, total, extraKids, extraAdults } =
      calculateTotals();

    const doc = createDocumentPdf();
    applyDocumentHeader(doc, "INVOICE");
    let infoY = 128;
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 40, infoY);
    infoY += 16;
    doc.text(`Event Date: ${eventDate || "-"}`, 40, infoY);
    infoY += 16;
    if (eventStartTime || eventEndTime) {
      const eventTimeLabel = [eventStartTime, eventEndTime]
        .filter(Boolean)
        .join(" - ");
      doc.text(`Event Time: ${eventTimeLabel}`, 40, infoY);
      infoY += 16;
    }
    doc.text(`Customer: ${customerName || "-"}`, 40, infoY);
    infoY += 16;
    if (childName) {
      doc.text(`Child: ${childName}`, 40, infoY);
      infoY += 16;
    }
    doc.text(`Phone: ${customerPhone || "-"}`, 40, infoY);
    infoY += 16;
    if (customerEmail) {
      doc.text(`Email: ${customerEmail}`, 40, infoY);
      infoY += 16;
    }

    const rows = [];
    if (pricingNeedsReview) {
      rows.push([`${historicalPackageName} - Agreed booking total (includes original tax and discounts)`, "1", formatMoney(subtotal)]);
    } else {
      rows.push([selectedPackage.name, "1", formatMoney(selectedPackage.price)]);
      if (describePackage(selectedPackage)) rows.push([describePackage(selectedPackage), "Included", ""]);
      if (guestCount(additionalKids) > 0) rows.push(["Additional Kids", String(guestCount(additionalKids)), formatMoney(extraKids)]);
      if (guestCount(additionalAdults) > 0) rows.push(["Additional Adults", String(guestCount(additionalAdults)), formatMoney(extraAdults)]);
      [...addOnSelections, ...customItems].forEach((item) => {
        rows.push([item.name, String(itemQuantity(item)), formatMoney(itemTotal(item))]);
      });
      rows.push(["", "", ""]);
      rows.push(["Subtotal", "", formatMoney(subtotal)]);
      rows.push([`Tax (${(TAX_RATE * 100).toFixed(2)}%)${removeTax ? " — REMOVED" : ""}`, "", formatMoney(tax)]);
      if (discount > 0) rows.push(["Discount", "", `-${formatMoney(discount).slice(1)}`]);
    }
    if (depositPaid > 0)
      rows.push(["Payments Already Applied", "", `-${formatMoney(depositPaid).slice(1)}`]);

    rows.push([
      "Balance (before card fee)",
      "",
      formatMoney(balanceBeforeCard),
    ]);
    if (cardFeeEnabled) {
      rows.push([
        `Card Processing Fee (${(CARD_FEE_RATE * 100).toFixed(0)}%)`,
        "",
        formatMoney(cardFee),
      ]);
    }
    rows.push(["Total Due", "", formatMoney(total)]);

    autoTable(doc, {
      startY: infoY + 12,
      head: [["Description", "Qty", "Price"]],
      body: rows,
      ...DOCUMENT_PDF_TABLE_OPTIONS,
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center", cellWidth: 60 },
        2: { halign: "right", cellWidth: 140 },
      },
    });

    if (notes.trim()) {
      const notesY = (doc.lastAutoTable?.finalY || infoY + 12) + 26;
      doc.setFontSize(12);
      doc.text("Notes", 40, notesY);
      doc.setFontSize(11);
      const noteLines = doc.splitTextToSize(notes.trim(), 530);
      doc.text(noteLines, 40, notesY + 20);
    }

    doc.setProperties({
      title: `Kiddie Kingdom Invoice - ${customerName || ""}`,
      subject: "Invoice",
      author: "Kiddie Kingdom Inc",
    });

    doc.save(
      `Kiddie_Kingdom_Invoice_${(customerName || "Customer").replace(
        /\s+/g,
        "_"
      )}_${new Date().toLocaleDateString().replace(/\//g, "-")}.pdf`
    );

    resetForm();
  };

  const { subtotal, tax, balanceBeforeCard, cardFee } =
    calculateTotals();

  return (
    <div className="invoice-generator">
      <h2 className="ig-title">Kiddie Kingdom Invoice Generator</h2>
      {prefillRequest?.data?.sourceEventId && (
        <div className="ig-prefill-banner">
          Prefilled from Event Calendar booking `{prefillRequest.data.sourceEventId}`. You
          can review and edit every field before generating the invoice.
        </div>
      )}

      <div className="ig-grid">
        {/* LEFT: FORM */}
        <div className="ig-card">
          <div className="ig-section">
            <h3 className="ig-section-title">Event & Customer</h3>
            <div className="ig-row">
              <div className="ig-field">
                <label>Event Date</label>
                <input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                />
              </div>
              <div className="ig-field">
                <label>Child's Name</label>
                <input
                  type="text"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  placeholder="e.g., Emma Doe"
                />
              </div>
            </div>

            <div className="ig-row">
              <div className="ig-field">
                <label>Start Time</label>
                <input
                  type="time"
                  value={eventStartTime}
                  onChange={(e) => setEventStartTime(e.target.value)}
                />
              </div>
              <div className="ig-field">
                <label>End Time</label>
                <input
                  type="time"
                  value={eventEndTime}
                  onChange={(e) => setEventEndTime(e.target.value)}
                />
              </div>
            </div>

            <div className="ig-row ig-row--name-phone">
              <div className="ig-field">
                <label>Customer Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="e.g., Jane Doe"
                />
              </div>
              <div className="ig-field">
                <label>Customer Phone</label>
                <input
                  type="text"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="e.g., 555-123-4567"
                />
              </div>
            </div>

            <div className="ig-row">
              <div className="ig-field">
                <label>Customer Email</label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="name@email.com"
                />
              </div>
            </div>
          </div>

          {pricingNeedsReview && (
            <div className="ig-section">
              <p>This booking has no saved pricing. Enter its agreed total from the original agreement, including original charges, tax and discounts. A card fee on the remaining payment can be added separately below.</p>
              <div className="ig-field">
                <label htmlFor="invoice-agreed-total">Agreed event total ($) — {historicalPackageName}</label>
                <input id="invoice-agreed-total" type="number" min="0" step="0.01" value={agreedEventTotal} onChange={(e) => setAgreedEventTotal(e.target.value)} />
              </div>
            </div>
          )}
          <fieldset disabled={pricingNeedsReview} className="ig-pricing-fields">
          <div className="ig-section">
            <h3 className="ig-section-title">Package & Guests</h3>

            <div className="ig-row">
              <div className="ig-field">
                <label>Select Package</label>
                <select
                  value={
                    selectedPackage
                      ? GENERATOR_PACKAGES.findIndex(
                          (p) => p.name === selectedPackage.name
                        )
                      : ""
                  }
                  onChange={(e) =>
                    setSelectedPackage(
                      e.target.value === ""
                        ? null
                        : GENERATOR_PACKAGES[Number(e.target.value)]
                    )
                  }
                >
                  <option value="">Select Package</option>
                  {GENERATOR_PACKAGES.map((pkg, index) => (
                    <option key={pkg.name} value={index}>
                      {pkg.name} — ${selectedPackage?.name === pkg.name ? selectedPackage.price : pkg.price}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selectedPackage && <p>{describePackage(selectedPackage)}</p>}
            <div className="ig-row">
              <div className="ig-field">
                <label>Additional Kids (${selectedPackage?.additionalChildPrice ?? ADDITIONAL_CHILD_PRICE} each, above included allowance)</label>
                <input
                  type="number"
                  min="0"
                  value={additionalKids}
                  onChange={(e) => setAdditionalKids(Number(e.target.value))}
                />
              </div>
              <div className="ig-field">
                <label>Additional Adults (${selectedPackage?.additionalAdultPrice ?? ADDITIONAL_ADULT_PRICE} each, above included allowance)</label>
                <input
                  type="number"
                  min="0"
                  value={additionalAdults}
                  onChange={(e) => setAdditionalAdults(Number(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="ig-section">
            <h3 className="ig-section-title">Add-Ons</h3>
            <div className="addons-list">
              {INVOICE_ADD_ONS.map((addOn) => {
                const selected = addOnSelections.find(
                  (i) => i.name === addOn.name
                );
                return (
                  <div
                    className={`add-on-group ${selected ? "is-selected" : ""}`}
                    key={addOn.name}
                  >
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={!!selected}
                        onChange={() => handleAddOnToggle(addOn.name)}
                      />
                      <span className="slider" />
                    </label>
                    <div className="addon-meta">
                      <div className="addon-name">{addOn.name}</div>
                      {!addOn.variable && (
                        <div className="addon-price">+ ${addOn.price}</div>
                      )}
                    </div>

                    {selected && (
                      <div className="addon-controls">
                        {addOn.variable && (
                          <div className="ig-field inline">
                            <label>Price ($)</label>
                            <input
                              type="number"
                              min="0"
                              value={selected.price}
                              onChange={(e) =>
                                handleAddOnPriceChange(
                                  addOn.name,
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        )}
                        <div className="ig-field inline">
                          <label>Qty</label>
                          <input
                            type="number"
                            min="1"
                            value={selected.quantity || 1}
                            onChange={(e) =>
                              handleAddOnQuantityChange(
                                addOn.name,
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ig-section">
            <h3 className="ig-section-title">Custom Items</h3>
            <div className="custom-row">
              <input
                type="text"
                placeholder="Item name (e.g., Socks for adults)"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
              />
              <input
                type="number"
                placeholder="Price"
                min="0"
                step="0.01"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
              />
              <input
                type="number"
                placeholder="Qty"
                min="1"
                value={customQty}
                onChange={(e) => setCustomQty(e.target.value)}
              />
              <button className="btn-secondary" onClick={addCustomItem}>
                + Add Item
              </button>
            </div>

            {customItems.length > 0 && (
              <ul className="custom-list">
                {customItems.map((ci) => (
                  <li key={ci.id} className="custom-chip">
                    <span>
                      {ci.name} — {ci.quantity} × {formatMoney(ci.price)}{" "}
                      <strong>= {formatMoney(ci.price * ci.quantity)}</strong>
                    </span>
                    <button
                      className="icon-btn"
                      onClick={() => removeCustomItem(ci.id)}
                      title="Remove"
                    >
                      ✖
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          </fieldset>

          <div className="ig-section">
            <h3 className="ig-section-title">Discounts & Payments</h3>
            <div className="ig-row">
              <div className="ig-field">
                <label>Discount ($)</label>
                <input
                  type="number"
                  min="0"
                  disabled={pricingNeedsReview}
                  value={discount}
                  onChange={(e) => setDiscount(Number(e.target.value))}
                />
              </div>
              <div className="ig-field">
                <label>Payments Already Applied ($, deposit included)</label>
                <input
                  type="number"
                  min="0"
                  value={depositPaid}
                  onChange={(e) => setDepositPaid(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="ig-row toggles-row">
              <label className="toggle">
                <input
                  type="checkbox"
                  disabled={pricingNeedsReview}
                  checked={removeTax}
                  onChange={(e) => setRemoveTax(e.target.checked)}
                />
                <span className="toggle-slider" />
                <span className="toggle-text">Remove Tax (10.25% only)</span>
              </label>

              <label className="toggle">
                <input
                  type="checkbox"
                  checked={cardFeeEnabled}
                  onChange={(e) => setCardFeeEnabled(e.target.checked)}
                />
                <span className="toggle-slider" />
                <span className="toggle-text">
                  Card Payment (add 3% on balance due)
                </span>
              </label>

              <label
                className="toggle"
                title="Use compression & smaller logo for email-friendly PDFs"
              >
                <input
                  type="checkbox"
                  checked={smallFileMode}
                  onChange={(e) => setSmallFileMode(e.target.checked)}
                />
                <span className="toggle-slider" />
                <span className="toggle-text">Small File Mode</span>
              </label>
            </div>
          </div>

          <div className="ig-section">
            <h3 className="ig-section-title">Notes</h3>
            <div className="ig-field">
              <label>Event Notes</label>
              <textarea
                className="ig-textarea"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any event or invoice notes here."
                rows={4}
              />
            </div>
          </div>

          <div className="ig-actions">
            <button className="btn-primary" onClick={generatePDF} disabled={pricingNeedsReview && !hasAgreedEventTotal(agreedEventTotal)}>
              Generate PDF Invoice
            </button>
          </div>
        </div>

        {/* RIGHT: SUMMARY */}
        <aside className="ig-summary">
          <div className="summary-card">
            <h3>Summary</h3>
            <div className="summary-row">
              <span>{pricingNeedsReview ? "Agreed Event Total" : "Subtotal"}</span>
              <strong>{formatMoney(subtotal)}</strong>
            </div>
            {!pricingNeedsReview && <div className="summary-row">
              <span>Tax {removeTax ? "(Removed)" : "(10.25%)"}</span>
              <strong>{formatMoney(tax)}</strong>
            </div>}
            {!pricingNeedsReview && discount > 0 && (
              <div className="summary-row">
                <span>Discount</span>
                <strong>-{formatMoney(discount)}</strong>
              </div>
            )}
            {depositPaid > 0 && (
              <div className="summary-row">
                <span>Payments Already Applied</span>
                <strong>-{formatMoney(depositPaid)}</strong>
              </div>
            )}
            <div className="summary-row">
              <span>Balance Before Card Fee</span>
              <strong>{formatMoney(balanceBeforeCard)}</strong>
            </div>
            {cardFeeEnabled && (
              <div className="summary-row">
                <span>Card Fee (3%)</span>
                <strong>{formatMoney(cardFee)}</strong>
              </div>
            )}

            <div className="summary-total">
              <div>Total Due Now</div>
              <div className="summary-amount">${calculateTotal()}</div>
            </div>
            {(eventStartTime || eventEndTime) && (
              <div className="summary-note">
                Event Time: {[eventStartTime, eventEndTime]
                  .filter(Boolean)
                  .join(" - ")}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default InvoiceGenerator;
